/**
 * Canonical deploy-tier resolution for observability (W0.3 · F16 / G12).
 *
 * ONE resolution order, used by every app that reports to Sentry or PostHog:
 *
 *     SENTRY_ENVIRONMENT → VITE_ENVIRONMENT → EXPO_PUBLIC_ENV → derived
 *
 * **`NODE_ENV` is never consulted.** It is pinned to `production` inside every
 * deployed container (frameworks switch on it for optimisations), so reading it
 * labels staging events `production` and mixes two tiers into one Sentry
 * project — the defect this module exists to end. `derived` is the caller's own
 * last-resort hint (the bundler's PROD flag, React Native's `__DEV__`), never a
 * process-wide framework variable.
 *
 * A resolved tier MUST be one of `development | staging | production | test`.
 * Anything else is a misconfiguration and fails LOUDLY — never a silent skip:
 *
 *  • `resolveObservabilityEnvironment` throws when an explicitly configured
 *    value is not one of the four.
 *  • The Sentry clients refuse to initialise when a DSN is present and the
 *    environment they were handed is not one of the four (see
 *    `assertObservabilityEnvironment` call sites in `errors/error-*.ts`).
 */

/** The only tiers observability accepts. */
export const OBSERVABILITY_ENVIRONMENTS = ['development', 'staging', 'production', 'test'] as const;

export type ObservabilityEnvironment = (typeof OBSERVABILITY_ENVIRONMENTS)[number];

/**
 * Environment candidates, in the order they are consulted.
 *
 * Callers pass what their runtime actually exposes — a Node process passes
 * `SENTRY_ENVIRONMENT`, a Vite build passes `VITE_ENVIRONMENT`, Expo passes
 * `EXPO_PUBLIC_ENV` — so no bundle ever has to touch `process.env` it cannot
 * see. Empty strings count as unset: `import.meta.env.VITE_ENVIRONMENT` is `''`
 * when the key is declared but blank.
 */
export interface ObservabilityEnvironmentSource {
  /** Canonical deploy-tier label for backend processes and containers. */
  SENTRY_ENVIRONMENT?: string | null;
  /** Vite-built SPAs (journey-web, business-web). */
  VITE_ENVIRONMENT?: string | null;
  /** Expo / React Native (citizen-mobile). */
  EXPO_PUBLIC_ENV?: string | null;
  /**
   * Last-resort hint when none of the keys above is set — e.g.
   * `import.meta.env.PROD ? 'production' : 'development'` or
   * `__DEV__ ? 'development' : 'production'`. NEVER `NODE_ENV`.
   */
  derived?: string | null;
}

/** Thrown when a tier label is not one of `OBSERVABILITY_ENVIRONMENTS`. */
export class ObservabilityEnvironmentError extends Error {
  constructor(
    readonly value: string,
    readonly source: string
  ) {
    super(
      `[observability] Refusing to report to an unknown environment "${value}" ` +
        `(from ${source}). Valid values: ${OBSERVABILITY_ENVIRONMENTS.join(' | ')}. ` +
        `NODE_ENV is deliberately NOT consulted — it is pinned to "production" in ` +
        `every deployed container, which is how staging events ended up in the ` +
        `production project.`
    );
    this.name = 'ObservabilityEnvironmentError';
  }
}

export function isObservabilityEnvironment(value: unknown): value is ObservabilityEnvironment {
  return (
    typeof value === 'string' && (OBSERVABILITY_ENVIRONMENTS as readonly string[]).includes(value)
  );
}

/** The key each candidate came from, for error messages. */
const SOURCE_KEYS = [
  'SENTRY_ENVIRONMENT',
  'VITE_ENVIRONMENT',
  'EXPO_PUBLIC_ENV',
  'derived',
] as const;

function firstConfigured(source: ObservabilityEnvironmentSource): {
  value: string;
  key: string;
} | null {
  for (const key of SOURCE_KEYS) {
    const raw = source[key];
    if (typeof raw !== 'string') continue;
    const trimmed = raw.trim();
    if (trimmed.length > 0) return { value: trimmed, key };
  }
  return null;
}

/**
 * Resolve the tier WITHOUT validating it — the honest raw answer, including a
 * misconfigured one. Used by the refusal paths so they can name the bad value.
 * Returns `development` when nothing at all is configured.
 */
export function readObservabilityEnvironment(source: ObservabilityEnvironmentSource = {}): string {
  return firstConfigured(source)?.value ?? 'development';
}

/**
 * Throw unless `value` is one of the four valid tiers.
 *
 * `context` names the caller (e.g. `initErrorTrackingNode`) so the failure
 * points at the process that is misconfigured.
 */
export function assertObservabilityEnvironment(
  value: string,
  context: string
): asserts value is ObservabilityEnvironment {
  if (!isObservabilityEnvironment(value)) {
    throw new ObservabilityEnvironmentError(value, context);
  }
}

/**
 * Resolve the tier and guarantee it is one of the four valid values.
 *
 * Throws `ObservabilityEnvironmentError` when a tier key IS configured but
 * carries something else — a typo in a deploy variable is a defect that must
 * surface at boot, not a reason to quietly mislabel every event.
 */
export function resolveObservabilityEnvironment(
  source: ObservabilityEnvironmentSource = {}
): ObservabilityEnvironment {
  const configured = firstConfigured(source);
  if (!configured) return 'development';
  if (!isObservabilityEnvironment(configured.value)) {
    throw new ObservabilityEnvironmentError(configured.value, configured.key);
  }
  return configured.value;
}
